/**
 * VÉRAULT Portal — Unified Files (Wave 35 · Phase 1 · PI-5)
 *
 * Single rollup of every file the firm has saved — across travel prep,
 * the suite experience, the concierge desk, treasury, agreements,
 * intake forms. Filter by source tab + linked record.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, Loader2, FileText, ExternalLink, Filter } from "lucide-react";
import {
  Surface,
  PortalButton,
  StatusTag,
  PageHeader,
  EditorialRule,
  AIWhisper,
  PortalBreadcrumb,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const SOURCE_TABS = [
  { value: "",                 label: "All" },
  { value: "travel_prep",      label: "Travel Prep" },
  { value: "suite_experience", label: "Suite" },
  { value: "concierge",        label: "Concierge" },
  { value: "treasury",         label: "Treasury" },
  { value: "agreements",       label: "Agreements" },
  { value: "forms",            label: "Forms" },
];

function FmtDate({ value }) {
  if (!value) return <span className="text-[hsl(var(--foreground-muted))]">—</span>;
  try {
    return <span>{new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>;
  } catch {
    return <span>{String(value)}</span>;
  }
}

function FileRow({ row }) {
  return (
    <Surface variant="paper" padded className="!p-3" data-testid={`files-row-${row.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <span className="inline-flex items-center justify-center size-9 rounded-full bg-[hsl(var(--background-stone))] text-[hsl(var(--foreground-muted))] shrink-0">
          <FileText className="size-4" strokeWidth={1.5} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-[15.5px]">{row.title}</p>
          <p className="font-mono text-[11px] text-[hsl(var(--foreground-muted))] mt-0.5">
            {row.source_tab || "general"}
            {row.linked_kind ? ` · ${row.linked_kind}` : ""}
            {row.trip_id ? ` · trip ${row.trip_id.slice(0, 8)}…` : ""}
            {" · "}<FmtDate value={row.created_at} />
          </p>
          {row.notes && (
            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-1">{row.notes}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {row.visible_to_client && <StatusTag tone="success">Client</StatusTag>}
          {(row.url || row.link_url) && (
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={() => window.open(row.url || row.link_url, "_blank", "noopener")}
              data-testid={`files-open-${row.id}`}
            >
              <ExternalLink className="size-3.5" /> Open
            </PortalButton>
          )}
        </div>
      </div>
    </Surface>
  );
}

export default function Files() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sourceTab, setSourceTab] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = [];
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      if (sourceTab) params.push(`source_tab=${encodeURIComponent(sourceTab)}`);
      const { data } = await api.get(`/files${params.length ? "?" + params.join("&") : ""}`);
      setRows(data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [sourceTab]);

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
     
  }, [q]);

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6" data-testid="files-page">
      <PortalBreadcrumb
        parent={{ to: "/portal/advisor", label: "Atelier" }}
        trail={[{ label: "Files" }]}
      />
      <PageHeader
        eyebrow="VÉRAULT · Files"
        title="Files"
        lede="One quiet ledger. Every document, every tab, every trip."
      />

      <motion.div variants={fadeInUp}>
        <Surface variant="paper" padded className="!p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Search className="size-4 text-[hsl(var(--foreground-muted))] shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, note, or tag…"
              className="flex-1 min-w-0 bg-transparent border-none outline-none font-mono text-[13px]"
              data-testid="files-search-input"
            />
            <Filter className="size-3.5 text-[hsl(var(--foreground-muted))]" />
            <select
              value={sourceTab}
              onChange={(e) => setSourceTab(e.target.value)}
              className="px-2 py-1.5 bg-[hsl(var(--background-paper))] border border-[hsl(var(--surface-hairline))] rounded font-mono text-[12px]"
              data-testid="files-source-filter"
            >
              {SOURCE_TABS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </Surface>
      </motion.div>

      {loading ? (
        <Surface variant="paper" padded>
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] flex items-center gap-2">
            <Loader2 className="size-3.5 animate-spin" /> Loading…
          </p>
        </Surface>
      ) : rows.length === 0 ? (
        <Surface variant="paper" padded>
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            No files match this filter yet.
          </p>
        </Surface>
      ) : (
        <motion.div variants={fadeInUp} className="space-y-2">
          {rows.map((r) => <FileRow key={r.id} row={r} />)}
        </motion.div>
      )}

      <EditorialRule />
      <AIWhisper label="A quiet note">
        Anything uploaded from a tab (Travel Prep · Suite Experience · Concierge Desk · Treasury) flows in here automatically.
        Toggle visible-to-client on individual files so the client only sees what you intend.
      </AIWhisper>
    </motion.div>
  );
}
